import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import styled from '@emotion/styled/macro'
import { useHistory } from 'react-router-dom'
import { Mutation } from 'react-apollo'
import { useTranslation } from 'react-i18next'
import EthVal from 'ethval'
import mq from 'mediaQuery'
import { COMMIT, REGISTER } from '../../../graphql/mutations'
import Tooltip from 'components/Tooltip/Tooltip'
import PendingTx from '../../PendingTx'
import Button from '../../Forms/Button'
import { ReactComponent as DefaultPencil } from '../../Icons/SmallPencil.svg'
import { ReactComponent as DefaultOrangeExclamation } from '../../Icons/OrangeExclamation.svg'
import { useAccount } from '../../QueryAccount'
import { Check, FavoriteOutlined } from '@material-ui/icons'
import useDarkMode from 'use-dark-mode'
import Loader from 'components/Loader'
import ReactMarkdown from 'react-markdown'
import { trackDomainRegister } from 'utils/analytics1'

const CTAContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border: 1px solid #5e17fe;
  border-radius: 4px;
  margin: 60px 0;
`

const Pencil = styled(DefaultPencil)`
  margin-right: 5px;
`

const Prompt = styled('span')`
  display: flex;
  align-items: center;
  color: #ffa600;
  margin-right: 10px;
  font-size: 9px;
`

const OrangeExclamation = styled(DefaultOrangeExclamation)`
  margin-right: 5px;
  height: 12px;
  width: 12px;
`

const Iconwrapper = styled('div')`
  display: flex;

  svg {
    margin-right: 10px;
  }
`
const RegisterWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 80px;

  ${mq.xLarge`
    flex-direction: row;
  `}
`

const RegisterTxt = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 133.4%;
  color: #4caf50;
  margin-bottom: 12px;
  text-align: center;

  ${mq.xLarge`
    margin-bottom: 0;
    margin-right: 22px;
  `}
`

const RevealTitle = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-size: 48px;
  font-weight: 700;
  line-height: 120%;
  text-align: center;
  letter-spacing: -0.5px;
  color: #5e17fe;
  margin-bottom: 22px;

  ${mq.xLarge`
    font-size: 60px;
  `}
`

const RevealSubTitle = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 36px;
  line-height: 116.7%;
  text-align: center;
  color: #5e17fe;
  margin-bottom: 50px;

  ${mq.xLarge`
    font-size: 48px;
  `}
`

const RevealTxt = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 133.4%;
  color: ${({ isDark }) => (isDark ? '#fff' : '#616161')};
  margin-bottom: 12px;

  ${mq.xLarge`
    margin-bottom: 0;
    margin-right: 22px;
  `}
`

function getCTA({
  step,
  incrementStep,
  secret,
  duration,
  label,
  hasSufficientBalance,
  txHash,
  setTxHash,
  setTimerRunning,
  setCommitmentTimerRunning,
  commitmentTimerRunning,
  setBlockCreatedAt,
  isAboveMinDuration,
  refetch,
  refetchIsMigrated,
  readOnly,
  price,
  history,
  t,
  ethUsdPrice,
  account,
  isDisabled,
  setIsDisabled,
  isDark,
  domainName,
  contentData
}) {
  const CTAs = {
    PRICE_DECISION: (
      <Mutation
        mutation={COMMIT}
        variables={{ label, secret, commitmentTimerRunning }}
        onCompleted={data => {
          const txHash = Object.values(data)[0]
          setTxHash(txHash)
          setCommitmentTimerRunning(true)
          incrementStep()
        }}
      >
        {mutate =>
          isAboveMinDuration && !readOnly ? (
            hasSufficientBalance ? (
              <Button
                data-testid="request-register-button"
                onClick={() => {
                  setIsDisabled(true)
                  mutate()
                }}
                disabled={step !== 'PRICE_DECISION' || isDisabled}
              >
                <Iconwrapper>
                  {/* <Check />
                  {t('register.buttons.request')} */}
                  {!isDisabled && <Check />}
                  {contentData?.requestRegisterButton?.title}
                  {isDisabled && (
                    <div style={{ marginLeft: 10 }}>
                      <Loader />
                    </div>
                  )}
                </Iconwrapper>
              </Button>
            ) : (
              <>
                <Prompt>
                  <OrangeExclamation />
                  {contentData?.insufficientBalanceText}
                </Prompt>
                <Button data-testid="request-register-button" type="disabled">
                  <Iconwrapper>
                    {!isDisabled && <Check />}
                    {contentData?.requestRegisterButton?.title}
                    {isDisabled && (
                      <div style={{ marginLeft: 10 }}>
                        <Loader />
                      </div>
                    )}
                  </Iconwrapper>
                </Button>
              </>
            )
          ) : readOnly ? (
            <Tooltip
              text="<p>You are not connected to a web3 browser. Please connect to a web3 browser and try again</p>"
              position="top"
              border={true}
              offset={{ left: -30, top: 10 }}
            >
              {({ tooltipElement, showTooltip, hideTooltip }) => {
                return (
                  <Button
                    data-testid="request-register-button"
                    type="disabled"
                    onMouseOver={() => {
                      showTooltip()
                    }}
                    onMouseLeave={() => {
                      hideTooltip()
                    }}
                  >
                    <Iconwrapper>
                      <Check />
                      {contentData?.requestRegisterButton?.title}
                    </Iconwrapper>
                  </Button>
                )
              }}
            </Tooltip>
          ) : (
            <Button data-testid="request-register-button" type="disabled">
              <Iconwrapper>
                <Check />
                {contentData?.requestRegisterButton?.title}
              </Iconwrapper>
            </Button>
          )
        }
      </Mutation>
    ),
    COMMIT_SENT: <PendingTx txHash={txHash} />,
    COMMIT_CONFIRMED: (
      <Button data-testid="disabled-register-button" type="disabled">
        <Iconwrapper>
          <Check />
          {contentData?.registerNowButton?.title}
        </Iconwrapper>
      </Button>
    ),
    AWAITING_REGISTER: (
      <Mutation
        mutation={REGISTER}
        variables={{ label, duration, secret }}
        onCompleted={data => {
          const txHash = Object.values(data)[0]
          setTxHash(txHash)
          incrementStep()
        }}
      >
        {mutate => (
          <>
            {hasSufficientBalance ? (
              <RegisterWrapper>
                <RegisterTxt>
                  {contentData?.clickRegisterText} {'>>'}
                </RegisterTxt>
                <Button
                  data-testid="register-button"
                  onClick={() => {
                    setIsDisabled(true)
                    mutate()
                  }}
                  disabled={isDisabled}
                >
                  <Iconwrapper>
                    {!isDisabled && <Check />}
                    {contentData?.registerNowButton?.title}
                    {isDisabled && (
                      <div style={{ marginLeft: 10 }}>
                        <Loader />
                      </div>
                    )}
                  </Iconwrapper>
                </Button>
              </RegisterWrapper>
            ) : (
              <RegisterWrapper>
                <Prompt>
                  <OrangeExclamation />
                  {contentData?.insufficientBalanceText}
                </Prompt>
                <Button data-testid="register-button" type="disabled">
                  <Iconwrapper>
                    <Check />
                    {contentData?.registerNowButton?.title}
                  </Iconwrapper>
                </Button>
              </RegisterWrapper>
            )}
          </>
        )}
      </Mutation>
    ),
    REVEAL_SENT: (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <PendingTx
          txHash={txHash}
          onConfirmed={async () => {
            incrementStep()
          }}
        />
      </div>
    ),
    REVEAL_CONFIRMED: (
      <RegisterWrapper>
        <RevealTxt isDark={isDark}>
          <ReactMarkdown>{contentData?.clickOnDetailsText}</ReactMarkdown>
        </RevealTxt>
        <DetailsButton
          data-testid="manage-name-button"
          onClick={async () => {
            setIsDisabled(true)

            // Add a new domain to local storage
            const domains = localStorage.getItem(`domains_ans_${account}`)
            const expDate = new Date()
            expDate.setFullYear(expDate.getFullYear() + 1)
            let data
            if (!domains)
              data = [
                {
                  domainName,
                  expiration: expDate
                }
              ]
            else {
              const arr = JSON.parse(domains)
              data = [
                ...arr,
                {
                  domainName,
                  expiration: expDate
                }
              ]
            }
            localStorage.setItem(`domains_ans_${account}`, JSON.stringify(data))

            // Redirect into detail section
            await Promise.all([refetch(), refetchIsMigrated()])
            history.push(
              `/name/${label}.${process.env.REACT_APP_REGISTRAR_TLD}`
            )
          }}
          disabled={isDisabled}
        >
          {!isDisabled && <Pencil />}
          <DetailsTxt>{contentData?.detailsButton?.title}</DetailsTxt>
          {isDisabled && (
            <div style={{ marginLeft: 10 }}>
              <Loader />
            </div>
          )}
        </DetailsButton>
      </RegisterWrapper>
    )
  }
  return CTAs[step]
}

const CTA = ({
  domain,
  loading,
  ethUsdPremiumPrice,
  underPremium,
  step,
  incrementStep,
  secret,
  duration,
  label,
  hasSufficientBalance,
  setTimerRunning,
  setCommitmentTimerRunning,
  commitmentTimerRunning,
  setBlockCreatedAt,
  isAboveMinDuration,
  refetch,
  refetchIsMigrated,
  readOnly,
  price,
  ethUsdPrice,
  contentData
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const account = useAccount()
  const [txHash, setTxHash] = useState(undefined)
  const darkMode = useDarkMode()
  const isDark = darkMode.value
  const [isDisabled, setIsDisabled] = useState(false)
  const [avaxPrices, setAvaxPrices] = useState('')

  const avaxName = domain?.name.split('.')
  const domainName = domain?.name

  useEffect(() => {
    fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=avalanche-2&vs_currencies=usd'
    )
      .then(response => response.json())
      .then(result => {
        setAvaxPrices(result['avalanche-2']['usd'])
        // setErrors('')
      })
      .catch(() => {
        // setErrors('API request failed. Please wait for a while and try again.')
        setAvaxPrices('')
      })
  }, [])

  let ethPrice, ethVal, basePrice, withPremium, usdPremium
  if (!loading && price) {
    ethVal = new EthVal(`${price}`).toEth()
    ethPrice = ethVal && ethVal.toFixed(3)
    if (ethUsdPrice && ethUsdPremiumPrice) {
      basePrice = ethVal.mul(ethUsdPrice) - ethUsdPremiumPrice
      withPremium =
        underPremium && ethUsdPremiumPrice
          ? `$${basePrice.toFixed(0)}(+$${ethUsdPremiumPrice.toFixed(2)}) =`
          : null
      usdPremium = ethVal.mul(ethUsdPrice).toFixed(2)
    } else if (ethUsdPrice) {
      usdPremium = ethVal.mul(ethUsdPrice).toFixed(2)
    }
  }

  useEffect(() => {
    setIsDisabled(false)
    if (step === 'REVEAL_CONFIRMED') {
      trackDomainRegister({
        label: avaxName[0],
        transactionId: txHash,
        type: 'register',
        price: (avaxPrices * ethPrice).toFixed(2)
      })
    }
  }, [step])

  return (
    <>
      {step === 'PRICE_DECISION' ? (
        <CTAContainer>
          <Wrapper>
            <Panel isBorder>
              <H4Bold style={{ marginBottom: 22 }}>
                {contentData?.domainAvailableText}
              </H4Bold>
              <H3Bold isDark={isDark} style={{ marginBottom: 34 }}>
                {avaxName[0]}.
                <span style={{ color: '#E7303C' }}>{avaxName[1]}</span>
              </H3Bold>
              <Flex>
                <FavoriteOutlined
                  style={{ color: '#c4c4c4', marginRight: 10 }}
                />
                <Caption isDark={isDark}>{contentData?.favoriteText}</Caption>
              </Flex>
            </Panel>
            <Panel>
              <Body1 style={{ marginBottom: 22 }}>
                {contentData?.foreverYoursText}
              </Body1>
              <H4Bold style={{ marginBottom: 22 }}>{ethPrice} AVAX</H4Bold>
              <H5Bold style={{ marginBottom: 22 }}>
                ${(avaxPrices * ethPrice).toFixed(2)}&nbsp;USD
              </H5Bold>
              {getCTA({
                step,
                incrementStep,
                secret,
                duration,
                label,
                hasSufficientBalance,
                txHash,
                setTxHash,
                setTimerRunning,
                setBlockCreatedAt,
                setCommitmentTimerRunning,
                commitmentTimerRunning,
                isAboveMinDuration,
                refetch,
                refetchIsMigrated,
                readOnly,
                price,
                history,
                t,
                ethUsdPrice,
                account,
                isDisabled,
                setIsDisabled,
                isDark,
                domainName,
                contentData
              })}
            </Panel>
          </Wrapper>
        </CTAContainer>
      ) : (
        <>
          {step === 'AWAITING_REGISTER' ? (
            <H4Bold style={{ marginTop: 40, marginBottom: 80 }}>
              {contentData?.completeRegisterationText}
            </H4Bold>
          ) : step === 'REVEAL_CONFIRMED' ? (
            <Layout>
              <RevealTitle>{contentData?.congratsText}</RevealTitle>
              <RevealSubTitle>{contentData?.congratsSubtitle}</RevealSubTitle>
            </Layout>
          ) : (
            <RevealSubTitle
              style={{ marginBottom: '9rem', marginTop: '10rem' }}
            >
              {contentData?.almostDoneText}
            </RevealSubTitle>
          )}
          {step !== 'COMMIT_CONFIRMED' &&
            step !== 'COMMIT_SENT' &&
            // step !== 'REVEAL_SENT' &&
            getCTA({
              step,
              incrementStep,
              secret,
              duration,
              label,
              hasSufficientBalance,
              txHash,
              setTxHash,
              setTimerRunning,
              setBlockCreatedAt,
              setCommitmentTimerRunning,
              commitmentTimerRunning,
              isAboveMinDuration,
              refetch,
              refetchIsMigrated,
              readOnly,
              price,
              history,
              t,
              ethUsdPrice,
              account,
              isDisabled,
              setIsDisabled,
              isDark,
              domainName,
              contentData
            })}
        </>
      )}
    </>
  )
}

export default CTA

const Wrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;

  ${mq.medium`
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 0;
    grid-template-rows: 1fr;
  `}
`

const Panel = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px 15px;

  ${({ isBorder }) => isBorder && `border-bottom: 1px solid #5E17FE;`}
  ${({ isBorder }) =>
    isBorder &&
    mq.medium`border-bottom: none; border-right: 1px solid #5E17FE;`}
`

const H4Bold = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 34px;
  line-height: 123.5%;
  text-align: center;
  letter-spacing: 0.25px;
  color: #5e17fe;
`

const H3Bold = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 40px;
  line-height: 116.7%;
  color: ${({ isDark }) => (isDark ? '#fff' : '#181818')};

  ${mq.medium`
    font-size: 48px;
  `}
`

const Caption = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 166%;
  letter-spacing: 0.4px;
  color: ${({ isDark }) => (isDark ? '#fff' : '#212121')};
`

const Body1 = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  letter-spacing: 0.15px;
  color: #e7303c;
`

const H5Bold = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 133.4%;
  color: #1b5e20;
`

const Flex = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`

const Layout = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const DetailsButton = styled('button')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 267px;
  height: 72px;
  background: #5e17fe;
  border: none;
  cursor: pointer;
  text-transform: capitalize;
`

const DetailsTxt = styled('div')`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 160%;
  letter-spacing: 0.15px;
  color: #ffffff;
  margin-left: 10px;
`
